import { useMemo } from "react";
import { addMinutes } from "date-fns";
import parseISO from "date-fns/parseISO";
import { DatabaseRow } from "@Shape-Digital/kudzu-data/lib/types/common";
import { AppointmentAIReportRowItem } from "../AppointmentAIReportsTableGrid/types";
import {
  getTimezoneOffsetMillis,
  UTCDateTimeToTimezone,
  isTimeZonePast,
} from "../../../common/dateHelpers";
import { DashboardAppointmentAIReport } from "../AppointmentAIReportsTableContainer/getAppointmentAIReports";
import useSystemConfig from "../../../hooks/useSystemConfig";
import getReportDeliveryDeadlineHours from "./getReportDeliveryDeadlineHours";
import { formatStudyNameWithSide } from "../../AppointmentOffering/utils/formatStudyNameWithSide";

const normalizeAIReport = ({
  appointmentAIReport,
  systemConfig,
}: {
  appointmentAIReport: DashboardAppointmentAIReport;
  systemConfig: DatabaseRow<"system_configs">;
}): AppointmentAIReportRowItem => {
  const { name, region, timezone } = appointmentAIReport.appointment.center;

  const appointmentOfferingPatientDetails =
    appointmentAIReport.appointment.appointment_patient_details.find(
      (d) => d.patient,
    );

  const { first_name, last_name, date_birth, mobile_phone_number, patient } =
    appointmentOfferingPatientDetails || {};

  const { mrn } = patient || {};

  const startedAtUTC = parseISO(
    appointmentAIReport.appointment.time_slot.started_at,
  );

  const isPast = isTimeZonePast({
    date: startedAtUTC,
    timezone,
  });

  const appointmentDurationMinutes =
    appointmentAIReport.appointment.appointment_offerings.reduce(
      (totalDuration, { center_offering }) => {
        return totalDuration + (center_offering.duration_minutes || 0);
      },
      0,
    );

  const readyToReadAtUTC = addMinutes(
    new Date(appointmentAIReport.appointment.time_slot.started_at),
    appointmentDurationMinutes,
  );

  const startedAtCenterTimezone = UTCDateTimeToTimezone(
    startedAtUTC,
    getTimezoneOffsetMillis(timezone),
  );

  const readyToReadAtCenterTimezone = readyToReadAtUTC
    ? UTCDateTimeToTimezone(readyToReadAtUTC, getTimezoneOffsetMillis(timezone))
    : null;

  const reportDeliveryDeadlineHours = getReportDeliveryDeadlineHours({
    appointmentAIReport,
    systemConfig,
  });

  const offeringStudyNames =
    appointmentAIReport.appointment.appointment_offerings.map((o) =>
      formatStudyNameWithSide(o.center_offering.name, o.side),
    );

  const studies = [...offeringStudyNames];

  const aiReport: AppointmentAIReportRowItem = {
    id: appointmentAIReport.id,
    status: appointmentAIReport.status || "",
    appointmentStatus: appointmentAIReport.appointment.status,
    appointmentPatient: {
      firstName: first_name || "",
      lastName: last_name || "",
      dateOfBirth: date_birth ? parseISO(date_birth) : null,
      phoneNumber: mobile_phone_number || "",
    },
    patient: {
      mrn: mrn || "",
    },
    hasPreviousAppointments:
      !!appointmentAIReport.appointment.has_previous_appointments,
    isAdditionalViews: !!appointmentAIReport.appointment.is_additional_views,
    isStat: !!appointmentAIReport.appointment.is_stat,
    isContactingPatientRequired:
      !!appointmentAIReport.appointment.is_contacting_patient_required,
    hasPriority: !!appointmentAIReport.appointment.has_priority,
    hasUnexpectedFindings: false, // AI reports don't have unexpected findings
    isUnexpectedFindingsReceiptConfirmed: false,
    startedAtUTC,
    startedAtCenterTimezone,
    readyToReadAtCenterTimezone,
    reportDeliveryDeadlineHours,
    center: {
      name,
      locale: region || "en-US",
      timezone,
    },
    appointment: {
      id: appointmentAIReport.appointment.id,
    },
    studies,
    createdAt: appointmentAIReport.created_at
      ? new Date(appointmentAIReport.created_at)
      : new Date(),
    isPast,
  };

  return aiReport;
};

const useRows = (appointmentAIReports: DashboardAppointmentAIReport[]) => {
  const {
    dataState: { data: systemConfig },
  } = useSystemConfig();

  const data = useMemo<AppointmentAIReportRowItem[]>(() => {
    try {
      if (!systemConfig) return [];
      const rows = appointmentAIReports.map((appointmentAIReport) =>
        normalizeAIReport({
          appointmentAIReport,
          systemConfig,
        }),
      );
      return rows;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("error", error);
    }

    return [];
  }, [appointmentAIReports, systemConfig]);

  return data;
};

export default useRows;
